/**
 * Minimal self-built dependency injection container (架构方案 §5.1).
 * Convention over configuration: no decorators, no reflection — just typed
 * service identifiers plus lazy singleton factories.
 */

export type ServiceIdentifier<T> = symbol & { __serviceBrand?: T };

export function createServiceIdentifier<T>(name: string): ServiceIdentifier<T> {
  return Symbol(name);
}

export type ServiceFactory<T> = (accessor: InstantiationService) => T;

export class ServiceCollection {
  private readonly instances = new Map<ServiceIdentifier<unknown>, unknown>();
  private readonly factories = new Map<
    ServiceIdentifier<unknown>,
    ServiceFactory<unknown>
  >();

  setInstance<T>(id: ServiceIdentifier<T>, instance: T): void {
    this.instances.set(id, instance);
  }

  setFactory<T>(id: ServiceIdentifier<T>, factory: ServiceFactory<T>): void {
    this.factories.set(id, factory);
  }

  getInstance<T>(id: ServiceIdentifier<T>): T | undefined {
    return this.instances.get(id) as T | undefined;
  }

  getFactory<T>(id: ServiceIdentifier<T>): ServiceFactory<T> | undefined {
    return this.factories.get(id) as ServiceFactory<T> | undefined;
  }

  has(id: ServiceIdentifier<unknown>): boolean {
    return this.instances.has(id) || this.factories.has(id);
  }
}

export class InstantiationService {
  constructor(
    private readonly collection: ServiceCollection = new ServiceCollection(),
  ) {}

  registerInstance<T>(id: ServiceIdentifier<T>, instance: T): void {
    this.collection.setInstance(id, instance);
  }

  registerSingleton<T>(
    id: ServiceIdentifier<T>,
    factory: ServiceFactory<T>,
  ): void {
    this.collection.setFactory(id, factory);
  }

  has(id: ServiceIdentifier<unknown>): boolean {
    return this.collection.has(id);
  }

  get<T>(id: ServiceIdentifier<T>): T {
    const existing = this.collection.getInstance(id);
    if (existing !== undefined) {
      return existing;
    }

    const factory = this.collection.getFactory(id);
    if (!factory) {
      throw new Error(`Service not registered: ${id.description ?? 'unknown'}`);
    }

    const instance = factory(this);
    this.collection.setInstance(id, instance);
    return instance;
  }
}
